import { useNavigate, useSearch } from "@tanstack/react-router";
import { api, ApiError } from "@signet/web-kit";
import { isPeriodKey, type PeriodKey } from "../shared/period.ts";
import type {
  CreatorsSettings,
  Dashboard,
  EssentialLink,
  OnboardingOverview,
  Pings,
  RecentNote,
  NotesContext,
  Roster,
  RosterLists,
  Trends,
} from "./types.ts";
import type { TemplateCategory } from "../shared/onboarding-types.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Every request the Creators pages make, and the keys they cache under.

   The hub kept its state in one component and patched it by hand after each
   write. Here the dashboard lives in the query cache instead, and each write
   patches the cached copy from the server's reply the same way the hub's
   setState did, so a tick or a rename moves on screen without a refetch.
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/creators";

export const creatorPath = (creator: string) => `${BASE}/creators/${encodeURIComponent(creator)}`;

export type Span = { period: PeriodKey; from: string | null; to: string | null };

export const keys = {
  all: ["creators"] as const,
  roster: ["creators", "roster"] as const,
  lists: ["creators", "lists"] as const,
  dashboards: (creator: string) => ["creators", "dashboard", creator] as const,
  dashboard: (creator: string, span: Span) =>
    ["creators", "dashboard", creator, span.period, span.from ?? "", span.to ?? ""] as const,
  trends: (creator: string, span: Span) =>
    ["creators", "trends", creator, span.period, span.from ?? "", span.to ?? ""] as const,
  pings: ["creators", "pings"] as const,
  notes: (q: string | undefined) => ["creators", "notes", q ?? ""] as const,
  notesAll: ["creators", "notes"] as const,
  overview: ["creators", "onboarding", "overview"] as const,
  template: ["creators", "onboarding", "template"] as const,
  links: ["creators", "links"] as const,
  settings: ["creators", "settings"] as const,
};

export function windowParams(span: Span, extra: Record<string, string | undefined> = {}): string {
  const params = new URLSearchParams({ period: span.period });
  if (span.period === "CUSTOM" && span.from && span.to) {
    params.set("from", span.from);
    params.set("to", span.to);
  }
  for (const [key, value] of Object.entries(extra)) if (value) params.set(key, value);
  return params.toString();
}

export const fetchRoster = () => api<Roster>(`${BASE}/roster`);
export const fetchLists = () => api<RosterLists>(`${BASE}/creators`);
export const fetchPings = () => api<Pings>(`${BASE}/pings`);
export const fetchOverview = () => api<OnboardingOverview>(`${BASE}/onboarding/overview`);
export const fetchTemplate = () => api<{ template: TemplateCategory[] }>(`${BASE}/onboarding/template`).then((r) => r.template);
export const fetchLinks = () => api<{ links: EssentialLink[] }>(`${BASE}/links`).then((r) => r.links);
export const fetchSettings = () => api<CreatorsSettings>(`${BASE}/settings`);

export function fetchDashboard(creator: string, span: Span, light: boolean) {
  return api<Dashboard>(`${creatorPath(creator)}?${windowParams(span, { light: light ? "1" : undefined })}`);
}

export function fetchTrends(creator: string, span: Span) {
  return api<Trends>(`${creatorPath(creator)}/trends?${windowParams(span)}`);
}

export function fetchNotes(q: string | undefined) {
  const params = new URLSearchParams({ limit: "80" });
  if (q) params.set("q", q);
  return api<{ notes: RecentNote[]; context: NotesContext }>(`${BASE}/notes?${params}`);
}

/**
 * The photo, which is the one write that is not JSON.
 *
 * `api()` sets a JSON content type, and a hand-set content type on a FormData
 * body drops the multipart boundary, so the server sees a body it cannot
 * parse. The browser writes the header itself here; only the CSRF header is
 * added.
 */
export async function uploadPhoto(creator: string, blob: Blob) {
  const body = new FormData();
  body.append("file", blob, "photo");
  let response: Response;
  try {
    response = await fetch(`${creatorPath(creator)}/photo`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-signet": "1", accept: "application/json" },
      body,
    });
  } catch {
    throw new ApiError(0, "Can't reach the Signet server. Check your connection.");
  }
  const payload = (await response.json().catch(() => ({}))) as { error?: string; photo?: string | null; profile?: Dashboard["profile"] };
  if (!response.ok) throw new ApiError(response.status, payload.error ?? "The photo could not be saved.");
  return payload as { photo: string | null; profile: Dashboard["profile"] };
}

/* ── search params ──────────────────────────────────────────────────────── */

/** A search param that is a non-empty string, or nothing. */
export function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

const day = (value: unknown) => {
  const v = str(value);
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
};

/** The creator page's window, read from the address so a reading can be sent to somebody. */
export const windowSearch = (raw: Record<string, unknown>) => ({
  period: isPeriodKey(raw.period) ? raw.period : undefined,
  from: day(raw.from),
  to: day(raw.to),
});

/**
 * Filters live in the URL, so the back button undoes one and a link is a
 * link. Empty values are dropped rather than written as `?q=`.
 */
export function useSearchState<T extends Record<string, string | undefined>>() {
  // Module routes are built at runtime, so the router's registered types only
  // know the core pages; a module page reads its own search loosely.
  const search = useSearch({ strict: false } as never) as T;
  const navigate = useNavigate();
  const set = (patch: Partial<T>, options: { replace?: boolean } = {}) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === "") delete next[key];
        return next;
      }) as never,
      replace: options.replace ?? false,
    });
  return [search, set] as const;
}
